/**
 * JDE-style filter inputs rendered as a second <tr> inside <thead>.
 *
 * columns: [{ key, label?, filterType: "text"|"select"|"number"|"date"|"none", options?: [{value,label}] }]
 * filters: { [key]: string }
 * onChange(key, value)
 * trailingCells: number of empty <th> after filters (e.g. Actions column)
 */
export default function ColumnFilterRow({
  columns = [],
  filters = {},
  onChange,
  trailingCells = 0,
  inputClassName = "w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#226DB4]/40 bg-white",
}) {
  return (
    <tr className="bg-white border-t border-slate-100 normal-case tracking-normal font-normal" data-testid="column-filter-row">
      {columns.map((col) => {
        const type = col.filterType || "text";
        if (type === "none") {
          return <th key={col.key} className="px-2 py-2" />;
        }
        return (
          <th key={col.key} className="px-2 py-2">
            {type === "select" ? (
              <select
                value={filters[col.key] || ""}
                onChange={(e) => onChange?.(col.key, e.target.value)}
                className={inputClassName}
                data-testid={`col-filter-${col.key}`}
              >
                <option value="">All</option>
                {(col.options || []).map((o) => {
                  const value = typeof o === "string" ? o : o.value;
                  const label = typeof o === "string" ? o : o.label;
                  return (
                    <option key={String(value)} value={value}>{label}</option>
                  );
                })}
              </select>
            ) : (
              <input
                type={type === "number" ? "number" : type === "date" ? "date" : "text"}
                min={type === "number" ? 0 : undefined}
                value={filters[col.key] || ""}
                onChange={(e) => onChange?.(col.key, e.target.value)}
                placeholder={type === "number" ? "Min" : "Filter…"}
                title={type === "number" ? "Minimum value (inclusive)" : undefined}
                className={`${inputClassName}${type === "number" ? " text-center" : ""}`}
                data-testid={`col-filter-${col.key}`}
              />
            )}
          </th>
        );
      })}
      {Array.from({ length: trailingCells }).map((_, i) => (
        <th key={`trail-${i}`} className="px-2 py-2" />
      ))}
    </tr>
  );
}

/**
 * Apply text / select / number(min) / date filters to a list of rows.
 * columnTypes: optional { [key]: "text"|"select"|"number"|"date"|"none" }
 * Select uses case-insensitive exact match; number uses min threshold;
 * date matches YYYY-MM-DD prefix; text uses contains.
 */
export function applyColumnFilters(rows, filters, getValue, columnTypes = {}) {
  const active = Object.entries(filters || {}).filter(([, v]) => String(v ?? "").trim() !== "");
  if (!active.length) return rows;
  return rows.filter((row) => {
    for (const [key, raw] of active) {
      const needle = String(raw).trim();
      const val = getValue(row, key);
      if (val === null || val === undefined) return false;
      const type = columnTypes[key] || (
        typeof val === "number" && needle !== "" && !Number.isNaN(Number(needle))
          ? "number"
          : "text"
      );
      if (type === "none") continue;
      if (type === "number") {
        const min = Number(needle);
        if (!Number.isFinite(min)) continue;
        const num = typeof val === "number" ? val : Number(val);
        if (!Number.isFinite(num) || num < min) return false;
        continue;
      }
      if (type === "select") {
        if (String(val).trim().toLowerCase() !== needle.toLowerCase()) return false;
        continue;
      }
      if (type === "date") {
        const hay = String(val).slice(0, 10);
        if (!hay || hay !== needle.slice(0, 10)) return false;
        continue;
      }
      if (!String(val).toLowerCase().includes(needle.toLowerCase())) return false;
    }
    return true;
  });
}

export function columnFiltersActive(filters) {
  return Object.values(filters || {}).some((v) => String(v ?? "").trim() !== "");
}
